(() => {
  const rootPath = window.Shopify?.routes?.root || '/';
  const productLandingUrl = (handle) => `${rootPath}products/${encodeURIComponent(handle || '')}`;
  const locale = window.SRThemeI18n?.locale === 'ur' ? 'ur-PK' : 'en-PK';
  const cleanTranslation = (value, fallback = '') => {
    if (!value || /translation missing:/i.test(String(value))) return fallback;
    return String(value);
  };
  const rawCartStrings = window.SRThemeI18n?.cart || {};
  const rawProductStrings = window.SRThemeI18n?.product || {};
  const cartStrings = Object.fromEntries(Object.entries(rawCartStrings).map(([key, value]) => [key, cleanTranslation(value)]));
  const productStrings = Object.fromEntries(Object.entries(rawProductStrings).map(([key, value]) => [key, cleanTranslation(value)]));
  const drawer = document.querySelector('[data-cart-drawer]');
  const toast = document.querySelector('.sr-toast');
  let lastFocusedElement = null;
  let toastTimer = null;

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'PKR';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        numberingSystem: 'latn',
        maximumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `Rs. ${(Number(cents || 0) / 100).toLocaleString(locale, { numberingSystem: 'latn' })}`;
    }
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const showToast = (message) => {
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = cleanTranslation(message, 'Please try again.');
    toast.hidden = false;
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 3200);
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
    });
  };

  const renderCart = (cart) => {
    if (!drawer) return;
    const itemsContainer = drawer.querySelector('[data-cart-items]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const total = drawer.querySelector('[data-cart-total]');

    updateCartCount(cart.item_count);

    if (!cart.items.length) {
      itemsContainer.innerHTML = `
        <div class="empty-state">
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 22V5M12 8c-3.7 0-6-2.2-6-5 3.7 0 6 2.2 6 5ZM12 13c-3.7 0-6-2.2-6-5 3.7 0 6 2.2 6 5ZM12 18c-3.7 0-6-2.2-6-5 3.7 0 6 2.2 6 5ZM12 10c3.7 0 6-2.2 6-5-3.7 0-6 2.2-6 5ZM12 15c3.7 0 6-2.2 6-5-3.7 0-6 2.2-6 5Z"/></svg>
          <h3>${escapeHtml(cartStrings.empty || 'Your cart is empty')}</h3>
          <p>${escapeHtml(cartStrings.emptyNote || 'Add your multigrain atta to get started.')}</p>
        </div>`;
      footer.hidden = true;
      return;
    }

    itemsContainer.innerHTML = cart.items.map((item, index) => {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" width="82" height="82" loading="lazy" alt="${escapeHtml(item.product_title)}">`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<small>${escapeHtml(item.variant_title)}</small>`
        : '';
      const properties = Object.entries(item.properties || {})
        .filter(([name, value]) => name.charAt(0) !== '_' && value !== '' && value != null)
        .map(([name, value]) => `<li><strong>${escapeHtml(name)}:</strong> ${escapeHtml(value)}</li>`)
        .join('');

      return `
        <article class="drawer-item" data-line="${index + 1}">
          ${image}
          <div class="drawer-item__details">
            <a href="${escapeHtml(productLandingUrl(item.handle))}">${escapeHtml(item.product_title)}</a>
            ${variant}
            ${properties ? `<ul class="line-properties">${properties}</ul>` : ''}
            <div class="drawer-item__foot">
              <span>${item.quantity} × ${money(item.final_price)}</span>
              <button type="button" data-cart-remove="${index + 1}">${escapeHtml(cartStrings.remove || 'Remove')}</button>
            </div>
          </div>
        </article>`;
    }).join('');

    total.textContent = money(cart.total_price);
    footer.hidden = false;
  };

  const getCart = async () => {
    const response = await fetch(`${rootPath}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(cartStrings.unableLoad || 'Unable to load cart');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const openCart = async (trigger) => {
    if (!drawer) return;
    lastFocusedElement = trigger || document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('is-locked');
    try {
      await getCart();
    } catch (error) {
      showToast(cartStrings.refreshError || 'Your cart could not be refreshed. Please try again.');
    }
    drawer.querySelector('[data-cart-close]')?.focus();
  };

  const closeCart = () => {
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('is-locked');
    lastFocusedElement?.focus?.();
  };

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-cart-open]');
    if (openTrigger) {
      event.preventDefault();
      openCart(openTrigger);
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeCart();
    }
  });

  drawer?.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('[data-cart-remove]');
    if (!removeButton) return;
    removeButton.disabled = true;

    try {
      const response = await fetch(`${rootPath}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line: Number(removeButton.dataset.cartRemove),
          quantity: 0
        })
      });
      if (!response.ok) throw new Error('Unable to remove item');
      renderCart(await response.json());
      showToast(cartStrings.itemRemoved || 'Item removed from cart.');
    } catch (error) {
      removeButton.disabled = false;
      showToast(cartStrings.removeError || 'The item could not be removed. Please try again.');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer && !drawer.hidden) closeCart();
  });

  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');

  menuToggle?.addEventListener('click', () => {
    const willOpen = mobileMenu.hidden;
    mobileMenu.hidden = !willOpen;
    menuToggle.setAttribute('aria-expanded', String(willOpen));
    menuToggle.innerHTML = willOpen
      ? '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m6 6 12 12M18 6 6 18"/></svg>'
      : '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h16"/></svg>';
  });

  mobileMenu?.addEventListener('click', (event) => {
    if (!event.target.closest('a')) return;
    mobileMenu.hidden = true;
    menuToggle?.setAttribute('aria-expanded', 'false');
  });

  document.querySelectorAll('[data-language-selector]').forEach((selector) => {
    selector.addEventListener('change', () => selector.form?.submit());
  });

  document.querySelectorAll('[data-quantity]').forEach((picker) => {
    const input = picker.querySelector('input');
    picker.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    picker.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      input.value = Number(input.value || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const form = productRoot.querySelector('.product-form');
    const variantSelect = productRoot.querySelector('[data-variant-select]');
    const variantInput = productRoot.querySelector('[data-variant-id]');
    const price = productRoot.querySelector('[data-product-price]');
    const compare = productRoot.querySelector('[data-product-compare]');
    const saveLabel = productRoot.querySelector('[data-save-label]');
    const addButton = productRoot.querySelector('[data-add-to-cart]');
    const addLabel = productRoot.querySelector('[data-add-label]');
    const productJson = productRoot.querySelector('[data-product-json]');
    const mediaTrack = productRoot.querySelector('[data-product-media-track]');
    const mediaSlides = [...productRoot.querySelectorAll('[data-product-media-slide]')];
    const mediaThumbnails = [...productRoot.querySelectorAll('[data-product-media-thumbnail]')];
    const urgencyPanel = productRoot.querySelector('[data-product-urgency]');
    const urgencyStock = urgencyPanel?.querySelector('[data-urgency-stock]');
    const readyLabel = cleanTranslation(addLabel?.textContent.trim(), productStrings.add || 'Add to Cart');

    const updateUrgency = (option) => {
      if (!urgencyPanel || !urgencyStock || !option) return;
      const available = option.dataset.available === 'true';
      const inventoryTracked = option.dataset.inventoryTracked === 'true';
      const inventory = Number(option.dataset.inventory || 0);
      if (!available) {
        urgencyStock.textContent = urgencyPanel.dataset.soldOutText || productStrings.soldOut || 'Sold out';
      } else if (inventoryTracked && inventory > 0 && inventory <= 10) {
        urgencyStock.textContent = (urgencyPanel.dataset.lowStockTemplate || 'Only __count__ packs shown in current inventory.').replace('__count__', inventory);
      } else if (inventoryTracked) {
        urgencyStock.textContent = urgencyPanel.dataset.availableText || 'Available now — ready for order processing.';
      } else {
        urgencyStock.textContent = urgencyPanel.dataset.freshText || 'Freshly packed in confirmed order sequence.';
      }
    };

    let activeMediaIndex = Math.max(0, mediaSlides.findIndex((slide) => slide.classList.contains('is-active')));
    let mediaScrollFrame = null;

    const activateMedia = (requestedIndex, shouldScroll = true, behavior = 'smooth') => {
      if (!mediaSlides.length) return;
      activeMediaIndex = (requestedIndex + mediaSlides.length) % mediaSlides.length;
      mediaSlides.forEach((slide, index) => {
        const active = index === activeMediaIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelector('video')?.pause();
      });
      mediaThumbnails.forEach((thumbnail, index) => {
        const active = index === activeMediaIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (shouldScroll && mediaTrack) {
        mediaTrack.scrollTo({ left: mediaSlides[activeMediaIndex].offsetLeft, behavior });
      }
    };

    mediaThumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => activateMedia(Number(thumbnail.dataset.mediaIndex || 0)));
    });

    productRoot.querySelector('[data-product-media-prev]')?.addEventListener('click', () => activateMedia(activeMediaIndex - 1));
    productRoot.querySelector('[data-product-media-next]')?.addEventListener('click', () => activateMedia(activeMediaIndex + 1));

    mediaTrack?.addEventListener('scroll', () => {
      window.cancelAnimationFrame(mediaScrollFrame);
      mediaScrollFrame = window.requestAnimationFrame(() => {
        if (!mediaTrack.clientWidth) return;
        activateMedia(Math.round(mediaTrack.scrollLeft / mediaTrack.clientWidth), false);
      });
    }, { passive: true });

    activateMedia(activeMediaIndex, true, 'auto');

    variantSelect?.addEventListener('change', () => {
      const option = variantSelect.selectedOptions[0];
      const selectedId = option.value;
      const priceValue = Number(option.dataset.price || 0);
      const compareValue = Number(option.dataset.compare || 0);
      const available = option.dataset.available === 'true';

      variantInput.value = selectedId;
      if (price) price.textContent = money(priceValue);

      if (compare && saveLabel && compareValue > priceValue) {
        compare.textContent = money(compareValue);
        compare.hidden = false;
        saveLabel.textContent = `${money(compareValue - priceValue)} ${productStrings.saved || 'saved'}`;
        saveLabel.hidden = false;
      } else {
        if (compare) compare.hidden = true;
        if (saveLabel) saveLabel.hidden = true;
      }

      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = available ? readyLabel : productStrings.soldOut || 'Sold out';
      updateUrgency(option);

      if (productJson) {
        try {
          const product = JSON.parse(productJson.textContent);
          const variant = product.variants.find((item) => String(item.id) === String(selectedId));
          const featuredMediaId = variant?.featured_media?.id;
          const mediaIndex = mediaSlides.findIndex((slide) => String(slide.dataset.mediaId) === String(featuredMediaId));
          if (mediaIndex >= 0) activateMedia(mediaIndex);
        } catch (error) {
          // The product remains fully usable if optional media switching fails.
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', selectedId);
      window.history.replaceState({}, '', url);
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!addButton || addButton.disabled) return;

      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      if (addLabel) addLabel.textContent = productStrings.adding || 'Adding…';

      try {
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || productStrings.addError || 'Unable to add item');

        await openCart(addButton);
        showToast(productStrings.added || 'Added to your cart.');
      } catch (error) {
        showToast(error.message || productStrings.addError || 'This item could not be added. Please try again.');
      } finally {
        addButton.removeAttribute('aria-busy');
        const selectedOption = variantSelect?.selectedOptions?.[0];
        const available = selectedOption ? selectedOption.dataset.available === 'true' : true;
        addButton.disabled = !available;
        if (addLabel) addLabel.textContent = available ? readyLabel : productStrings.soldOut || 'Sold out';
      }
    });
  });

  const catalogPage = document.querySelector('.catalog-page');
  const catalogOverview = document.querySelector('[data-catalog-overview]');
  const catalogOverviewBreadcrumb = document.querySelector('[data-catalog-overview-breadcrumb]');
  const landingPages = [...document.querySelectorAll('[data-product-landing-page]')];
  const catalogDocumentTitle = document.title;
  const getLandingPage = (handle) => landingPages.find((page) => page.dataset.productHandle === handle);

  const updateLandingUrl = (handle, mode = 'push') => {
    const url = new URL(window.location.href);
    if (handle) {
      url.searchParams.set('product', handle);
      url.searchParams.delete('variant');
      url.hash = `CatalogProductLanding-${handle}`;
    } else {
      url.searchParams.delete('product');
      url.searchParams.delete('variant');
      url.hash = '';
    }
    const state = handle ? { product: handle } : {};
    if (mode === 'replace') window.history.replaceState(state, '', url);
    else window.history.pushState(state, '', url);
  };

  const showCatalogOverview = ({ updateUrl = false, scroll = false } = {}) => {
    if (!landingPages.length) return;
    landingPages.forEach((page) => { page.hidden = true; });
    if (catalogOverview) catalogOverview.hidden = false;
    if (catalogOverviewBreadcrumb) catalogOverviewBreadcrumb.hidden = false;
    catalogPage?.classList.remove('has-active-product');
    document.title = catalogDocumentTitle;
    if (updateUrl) updateLandingUrl('', 'push');
    if (scroll) catalogPage?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const showProductLanding = (handle, { updateUrl = false, scroll = false } = {}) => {
    const activePage = getLandingPage(handle);
    if (!activePage) return false;
    landingPages.forEach((page) => { page.hidden = page !== activePage; });
    if (catalogOverview) catalogOverview.hidden = true;
    if (catalogOverviewBreadcrumb) catalogOverviewBreadcrumb.hidden = true;
    catalogPage?.classList.add('has-active-product');
    document.title = `${activePage.dataset.productTitle} – ${document.title.split('–').pop().trim()}`;
    if (updateUrl) updateLandingUrl(handle, 'push');
    if (scroll) catalogPage?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const activeMedia = activePage.querySelector('[data-product-media-slide].is-active');
    const track = activePage.querySelector('[data-product-media-track]');
    if (activeMedia && track) track.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
    return true;
  };

  document.addEventListener('click', (event) => {
    const landingLink = event.target.closest('[data-product-landing-link]');
    if (landingLink && getLandingPage(landingLink.dataset.productLandingLink)) {
      event.preventDefault();
      showProductLanding(landingLink.dataset.productLandingLink, { updateUrl: true, scroll: true });
      return;
    }

    const backLink = event.target.closest('[data-product-landing-back]');
    if (backLink) {
      event.preventDefault();
      showCatalogOverview({ updateUrl: true, scroll: true });
    }
  });

  const syncLandingFromUrl = () => {
    const handle = new URL(window.location.href).searchParams.get('product');
    if (!handle || !showProductLanding(handle)) showCatalogOverview();
  };

  window.addEventListener('popstate', syncLandingFromUrl);
  syncLandingFromUrl();

  window.SRThemeCart = {
    open: openCart,
    close: closeCart,
    refresh: getCart,
    showToast,
    money
  };
})();
